import { useEffect, useMemo, useRef } from 'react';
import { IntervalSet, coverageBuckets } from '../../core/ranges';
import type { ByteSpan } from '../../core/types';
import { formatBytes, formatPercent } from '../../core/format';

interface Props {
  /** Total file size, or null when the host did not say. */
  total: number | null;
  /** Spans that are saved and readable. */
  readable: readonly ByteSpan[];
  /** Spans currently being written. Only the leading edge is drawn. */
  inFlight?: readonly ByteSpan[];
  /** Where the player is reading right now (bytes). */
  feed?: number | null;
  variant?: 'full' | 'mini';
  label?: string;
}

const MARK_H = 7;

function readVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * The whole file as one strip of bytes. Saved ranges fill in whatever order
 * they arrive, so a jump shows up as a separate island; the marker above the
 * strip is the byte the player is reading.
 */
export function CoverageRail({
  total,
  readable,
  inFlight = [],
  feed = null,
  variant = 'full',
  label,
}: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const raf = useRef(0);

  const set = useMemo(() => IntervalSet.from(readable), [readable]);
  const saved = set.measure();
  const fraction = total && total > 0 ? Math.min(1, saved / total) : null;
  const pieces = set.spans.length;

  useEffect(() => {
    const el = canvas.current;
    const box = wrap.current;
    if (!el || !box || total === null || total <= 0) return;

    const draw = () => {
      raf.current = 0;
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const w = Math.max(1, Math.floor(box.clientWidth));
      const railH = variant === 'mini' ? 6 : 12;
      const markH = variant === 'mini' ? 0 : MARK_H;
      const h = railH + markH;
      if (el.width !== Math.floor(w * dpr) || el.height !== Math.floor(h * dpr)) {
        el.width = Math.floor(w * dpr);
        el.height = Math.floor(h * dpr);
        el.style.width = `${w}px`;
        el.style.height = `${h}px`;
      }
      const ctx = el.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const hole = readVar(el, '--rail-hole', '#E8E6E1');
      const savedC = readVar(el, '--rail-saved', '#202226');
      const flight = readVar(el, '--rail-flight', '#E39A1B');
      const feedC = readVar(el, '--rail-feed', '#8A5600');
      const radius = railH / 2;

      ctx.save();
      ctx.beginPath();
      ctx.roundRect(0, markH, w, railH, radius);
      ctx.clip();
      ctx.fillStyle = hole;
      ctx.fillRect(0, markH, w, railH);

      const cols = Math.max(1, w);
      const buckets = coverageBuckets(set, total, cols);
      ctx.fillStyle = savedC;
      for (let x = 0; x < cols; x++) {
        const v = buckets[x] ?? 0;
        if (v <= 0.001) continue;
        ctx.globalAlpha = v > 0.97 ? 1 : 0.28 + 0.72 * v;
        ctx.fillRect(x, markH, 1.02, railH);
      }
      ctx.globalAlpha = 1;

      // The writing edge of each active request.
      ctx.fillStyle = flight;
      for (const s of inFlight) {
        const x = (s.start / total) * w;
        ctx.fillRect(Math.min(w - 3, Math.max(0, x)), markH, 3, railH);
      }
      ctx.restore();

      if (feed !== null && markH > 0) {
        const fx = Math.min(w - 4, Math.max(4, (feed / total) * w));
        ctx.fillStyle = feedC;
        ctx.beginPath();
        ctx.moveTo(fx - 4, 0);
        ctx.lineTo(fx + 4, 0);
        ctx.lineTo(fx, MARK_H - 1);
        ctx.closePath();
        ctx.fill();
      }
    };

    const schedule = () => {
      if (!raf.current) raf.current = requestAnimationFrame(draw);
    };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(box);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', schedule);
    return () => {
      ro.disconnect();
      mq.removeEventListener('change', schedule);
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
    };
  }, [set, total, inFlight, feed, variant]);

  const summary =
    total === null
      ? `${formatBytes(saved)} saved. Total size not reported by the host.`
      : `${formatPercent(fraction)} of ${formatBytes(total)} saved in ${pieces} ${pieces === 1 ? 'piece' : 'pieces'}.`;

  if (total === null || total <= 0) {
    return (
      <div
        className={`rail rail--${variant} rail--unknown`}
        role="img"
        aria-label={label ? `${label}. ${summary}` : summary}
      >
        <span className="rail__unknown" />
      </div>
    );
  }
  return (
    <div
      ref={wrap}
      className={`rail rail--${variant}`}
      role="img"
      aria-label={label ? `${label}. ${summary}` : summary}
    >
      <canvas ref={canvas} aria-hidden="true" />
    </div>
  );
}
